import { useEffect } from 'react'
import {
    AnimatePresence,
    motion,
    useDragControls,
    useMotionValue,
    useTransform,
} from 'framer-motion'
import Portal from 'components/portal'

interface CustomBottomSheetProps {
    isOpen: boolean
    children: React.ReactNode
    onDismiss: () => void
    /**
     * Drag to dismiss from the handle only. Needed when the content scrolls:
     * a sheet that drags from anywhere claims every vertical swipe on touch,
     * so the list inside it can never be scrolled.
     */
    dragHandleOnly?: boolean
}
const CustomBottomSheet = ({
    isOpen,
    children,
    onDismiss,
    dragHandleOnly = false,
}: CustomBottomSheetProps) => {
    const dragControls = useDragControls()
    const dragY = useMotionValue(0)
    const sheetY = useTransform(
        dragY,
        [0, window.innerHeight],
        [0, window.innerHeight]
    )

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'auto'
        }
    }, [isOpen])

    const handleDragEnd = (_, info) => {
        const { offset, velocity } = info
        if (offset.y > 100 || velocity.y > 500) {
            onDismiss()
        }
    }

    return (
        <Portal>
            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.6 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="fixed inset-0 bg-black z-40 "
                            onClick={onDismiss}
                        />
                        <motion.div
                            initial={{ translateY: '100%', opacity: 0 }}
                            animate={{ translateY: '0%', opacity: 1 }}
                            exit={{ translateY: '100%', opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="fixed sm:left-0 bottom-0 w-full md:right-0 md:mx-auto md:max-w-[560px] bg-white shadow-pop z-50 pt-3 pb-[calc(1rem+var(--safe-bottom))] px-4 rounded-t-sheet"
                            style={{ y: sheetY }}
                            drag="y"
                            dragControls={dragControls}
                            dragListener={!dragHandleOnly}
                            dragConstraints={{ top: 0, bottom: 0 }}
                            onDragEnd={handleDragEnd}
                            dragElastic={0.2}
                        >
                            {dragHandleOnly ? (
                                <div
                                    className="-mt-3 mb-1 cursor-grab touch-none py-3"
                                    onPointerDown={(event) =>
                                        dragControls.start(event)
                                    }
                                >
                                    <div className="mx-auto h-1.5 w-10 rounded-full bg-stroke" />
                                </div>
                            ) : (
                                <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-stroke" />
                            )}
                            {children}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </Portal>
    )
}

export default CustomBottomSheet
